/**
 * The wire shapes `Rws.AiInsights.Host.Administration.EntitlementAdminEndpoints` returns (phases 15 and 21,
 * `AIA-81`): classify a person, administer `SeatCost` rows, and
 * write a person's organisational associations. Everything here is gated on `entitlement.administer`.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** The result of classifying a person (`Rws.AiInsights.Host.Administration.PersonClassificationResponse`). */
export interface PersonClassificationResult {
  personId: string;
  previousStatus: string;
  newStatus: string;
}

/** The result of updating a person's display name. */
export interface PersonDisplayNameResult {
  personId: string;
  displayName: string;
}

/** A hierarchical group of which a person is a direct member. */
export interface PersonGroup {
  id: string;
  name: string;
  /** True when this membership carries the person's monthly allowance. */
  providesAllowance: boolean;
}

/** Direct hierarchical group memberships for a person. */
export type PersonGroups = PersonGroup[];

/** A maintained Division or Department value. */
export interface AssociationValue {
  id: string;
  kind: string;
  value: string;
  associatedUserCount: number;
  /** The parent Division id of a Department; absent for other kinds. */
  parentId?: string | null;
  /** The Backstage name the value was imported under; read-only in the portal. */
  backstageName?: string | null;
  /** The recorded manager's organisational address; read-only in the portal. */
  managerEmail?: string | null;
}

/** Calls `GET /api/users/{personId}/groups`. */
export async function fetchPersonGroups(personId: string): Promise<AdminResult<PersonGroups>> {
  return adminRequest<PersonGroups>(`/api/users/${encodeURIComponent(personId)}/groups`, { method: "GET" }, "person groups");
}

/** Association kinds whose values can be maintained by administrators. */
export type ManagedAssociationKind = "Division" | "Department";

/** A supported ordering key for the admin people list. */
export type PersonSortField = "email" | "displayName" | "division" | "department" | "status" | "createdAt";

/** The result of assigning an association to a set of users by email. */
export interface BulkPersonAssociationResult {
  updatedCount: number;
  notFoundEmails: string[];
}

/** One `SeatCost` row. */
export interface SeatCost {
  id: string;
  vendorId: string;
  amount: number;
  currency: string;
  effectiveFrom: string;
}

/** Calls `POST /api/users/associations/bulk` for a list of email addresses. */
export async function setPersonAssociationsBulk(
  emails: string[],
  kind: ManagedAssociationKind,
  value: string,
): Promise<AdminResult<BulkPersonAssociationResult>> {
  return adminRequest<BulkPersonAssociationResult>(
    "/api/users/associations/bulk",
    { method: "POST", body: JSON.stringify({ emails, kind, value }) },
    "bulk association update",
  );
}

/** One organisational association (division/department/group). */
export interface PersonAssociation {
  personId: string;
  kind: string;
  value: string;
}

/** One person's current division, department and group (`Rws.AiInsights.Host.Administration.PersonAssociationsResponse`). Any may be `null` if never recorded. */
export interface PersonAssociations {
  personId: string;
  division: string | null;
  department: string | null;
  group: string | null;
}

/** One row of the person browse list (`Rws.AiInsights.Host.Administration.PersonSummaryResponse`). */
export interface PersonSummary {
  id: string;
  email: string;
  displayName: string;
  status: string;
  createdAt: string;
  division?: string | null;
  department?: string | null;
  /** True when the caller holds `entitlement.administer`, so Manage (the administration page) is offered. Everyone listed is already in the caller's scope. */
  canAdminister: boolean;
}

/** One page of the person browse list (`Rws.AiInsights.Host.Administration.PersonListPageResponse`). */
export interface PersonListPage {
  items: PersonSummary[];
  totalCount: number;
}

function personClassificationPath(personId: string): string {
  return `/api/users/${encodeURIComponent(personId)}/classification`;
}

function personAssociationPath(personId: string, kind: string): string {
  return `/api/users/${encodeURIComponent(personId)}/associations/${encodeURIComponent(kind)}`;
}

function personAssociationsPath(personId: string): string {
  return `/api/users/${encodeURIComponent(personId)}/associations`;
}

const seatCostsPath = "/api/entitlement/seat-costs";

function associationCataloguePath(kind: ManagedAssociationKind): string {
  return `/api/entitlement/associations/${encodeURIComponent(kind)}/catalogue`;
}

/** Calls `POST /api/users/{personId}/classification`. */
export async function classifyPerson(personId: string, status: string): Promise<AdminResult<PersonClassificationResult>> {
  return adminRequest<PersonClassificationResult>(personClassificationPath(personId), { method: "POST", body: JSON.stringify({ status }) }, "classification");
}

/** Calls `PUT /api/users/{personId}/display-name`. */
export async function setPersonDisplayName(personId: string, displayName: string): Promise<AdminResult<PersonDisplayNameResult>> {
  return adminRequest<PersonDisplayNameResult>(
    `/api/users/${encodeURIComponent(personId)}/display-name`,
    { method: "PUT", body: JSON.stringify({ displayName }) },
    "display name update",
  );
}

/** Calls `GET /api/entitlement/associations/{kind}/catalogue`. */
export async function fetchAssociationCatalogue(kind: ManagedAssociationKind): Promise<AdminResult<AssociationValue[]>> {
  return adminRequest<AssociationValue[]>(associationCataloguePath(kind), { method: "GET" }, "organisation values");
}

/** A person associated with a Division or Department, including through its groups. */
export interface AssociationUser {
  email: string;
  displayName: string;
}

/** The distinct people associated with a Division or Department, with their count. */
export interface AssociationUsers {
  count: number;
  users: AssociationUser[];
  /** Monthly USD allowance if every active group member spent their full effective allowance, each counted once; null when there are no active group members. */
  totalAllowanceUsd: number | null;
  /** The people directly associated with the unit (not counting group members). */
  directCount: number;
}

/** Calls `GET /api/entitlement/associations/{kind}/catalogue/{id}/users`. Requires entitlement.administer; the list is employee data. */
export async function fetchAssociationUsers(kind: ManagedAssociationKind, id: string): Promise<AdminResult<AssociationUsers>> {
  return adminRequest<AssociationUsers>(`${associationCataloguePath(kind)}/${encodeURIComponent(id)}/users`, { method: "GET" }, "organisation users");
}

/** Calls `POST /api/entitlement/associations/{kind}/catalogue`. */
export async function createAssociationValue(kind: ManagedAssociationKind, value: string, parentId?: string): Promise<AdminResult<AssociationValue>> {
  return adminRequest<AssociationValue>(associationCataloguePath(kind), { method: "POST", body: JSON.stringify({ value, parentId }) }, "organisation value creation");
}

/** Calls `PUT /api/entitlement/associations/{kind}/catalogue/{id}`. */
export async function renameAssociationValue(kind: ManagedAssociationKind, id: string, value: string, parentId?: string): Promise<AdminResult<AssociationValue>> {
  return adminRequest<AssociationValue>(`${associationCataloguePath(kind)}/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ value, parentId }) }, "organisation value rename");
}

/** Calls `DELETE /api/entitlement/associations/{kind}/catalogue/{id}`. */
export async function deleteAssociationValue(kind: ManagedAssociationKind, id: string): Promise<AdminResult<undefined>> {
  return adminRequest<undefined>(`${associationCataloguePath(kind)}/${encodeURIComponent(id)}`, { method: "DELETE" }, "organisation value deletion");
}

/** Calls `GET /api/users`, optionally filtered to one status, a partial email/display-name match, or a specific role membership. */
export async function fetchPeople(
  status: string | undefined,
  search: string | undefined,
  offset: number,
  pageSize: number,
  roleId?: string,
  sortBy?: PersonSortField,
  sortDescending = true,
): Promise<AdminResult<PersonListPage>> {
  const params = new URLSearchParams({ offset: String(offset), pageSize: String(pageSize) });
  if (status) {
    params.set("status", status);
  }

  if (search) {
    params.set("search", search);
  }

  if (roleId) {
    params.set("roleId", roleId);
  }

  if (sortBy) {
    params.set("sortBy", sortBy);
  }

  params.set("sortDescending", String(sortDescending));

  return adminRequest<PersonListPage>(`/api/users?${params.toString()}`, { method: "GET" }, "people");
}

/** Calls `GET /api/users?includeAll=true` for the Users page's client-side data table. */
export async function fetchAllPeople(): Promise<AdminResult<PersonListPage>> {
  return adminRequest<PersonListPage>("/api/users?includeAll=true", { method: "GET" }, "people");
}


/** Calls `GET /api/entitlement/seat-costs`. */
export async function fetchSeatCosts(): Promise<AdminResult<SeatCost[]>> {
  return adminRequest<SeatCost[]>(seatCostsPath, { method: "GET" }, "seat costs");
}

/** Calls `POST /api/entitlement/seat-costs`. */
export async function createSeatCost(vendorId: string, amount: number, currency: string, effectiveFrom: string): Promise<AdminResult<SeatCost>> {
  return adminRequest<SeatCost>(seatCostsPath, { method: "POST", body: JSON.stringify({ vendorId, amount, currency, effectiveFrom }) }, "seat cost creation");
}

/** Calls `PUT /api/users/{personId}/associations/{kind}`. `kind` is one of `Division`, `Department`, or `Group`. */
export async function setPersonAssociation(personId: string, kind: string, value: string): Promise<AdminResult<PersonAssociation>> {
  return adminRequest<PersonAssociation>(personAssociationPath(personId, kind), { method: "PUT", body: JSON.stringify({ value }) }, "association update");
}

/** Calls `GET /api/users/{personId}/associations`. */
export async function fetchPersonAssociations(personId: string): Promise<AdminResult<PersonAssociations>> {
  return adminRequest<PersonAssociations>(personAssociationsPath(personId), { method: "GET" }, "person associations");
}

/** One provider the portal knows, as a seat cost can be recorded for it. */
export interface ProviderOption {
  vendorId: string;
  displayName: string;
}

/** Calls `GET /api/entitlement/providers`. */
export async function fetchProviderOptions(): Promise<AdminResult<ProviderOption[]>> {
  return adminRequest<ProviderOption[]>("/api/entitlement/providers", { method: "GET" }, "providers");
}

/** The owner recorded for a group, Division or Department. Group Owner follows from ownership, so no role is reported here. */
export interface UnitOwnerResponse {
  managerEmail: string | null;
}

/** Calls `PUT /api/entitlement/associations/{kind}/catalogue/{id}/owner`. Saves on the call; refused with 409 for a value imported from Backstage. */
export async function setAssociationOwner(kind: ManagedAssociationKind, id: string, personId: string | null): Promise<AdminResult<UnitOwnerResponse>> {
  return adminRequest<UnitOwnerResponse>(`${associationCataloguePath(kind)}/${encodeURIComponent(id)}/owner`, { method: "PUT", body: JSON.stringify({ personId }) }, "organisation owner");
}
